import type { ComponentChildren, JSX } from 'preact';
import type { ComponentType } from 'preact';
import type { LucideProps } from 'lucide-preact';
import { Loader2 } from 'lucide-preact';
import { cn } from '@/lib/cn';
import { Icon } from './Icon';

export type ButtonVariant = 'primary' | 'default' | 'ghost' | 'danger' | 'subtle';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<
  JSX.ButtonHTMLAttributes<HTMLButtonElement>,
  'size' | 'loading' | 'icon'
> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leftIcon?: ComponentType<LucideProps>;
  rightIcon?: ComponentType<LucideProps>;
  children?: ComponentChildren;
  fullWidth?: boolean;
}

const VARIANT: Record<ButtonVariant, string> = {
  // Refresh 2026-05-20: primary y default ganan micro-translate-y de
  // 1px on hover + sombra superior reforzada → sensación "lift" estilo
  // Linear/Vercel.  Ghost/danger/subtle quedan flat porque su rol es
  // "acción contextual ligera" y un lift les daría demasiado peso.
  primary:
    'bg-brand text-brand-contrast border border-brand hover:bg-brand-hover hover:border-brand-hover hover:-translate-y-px hover:shadow-sm shadow-xs active:translate-y-0 active:bg-brand-active active:shadow-xs',
  default:
    'bg-surface text-fg border border-border hover:bg-surface-hover hover:border-border-strong hover:text-fg-strong hover:-translate-y-px hover:shadow-sm shadow-xs active:translate-y-0 active:bg-surface-active active:shadow-none',
  ghost:
    'bg-transparent text-fg-muted border border-transparent hover:bg-surface-hover hover:text-fg-strong shadow-none active:bg-surface-active',
  danger:
    'bg-transparent text-error border border-error hover:bg-error-bg hover:border-error shadow-none active:bg-error-bg',
  subtle:
    'bg-surface-2 text-fg border border-border-subtle hover:bg-surface-hover hover:text-fg-strong shadow-none active:bg-surface-active',
};

const SIZE: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs font-medium gap-1.5',
  md: 'h-9 px-4 text-13 font-semibold gap-2',
  lg: 'h-11 px-5 text-sm font-semibold gap-2',
};

const BASE =
  // El `transform` se añade a la lista de transitions para que el
  // hover:-translate-y-px anime correctamente.  Sin él, el desplazamiento
  // sería instantáneo.
  'inline-flex items-center justify-center font-semibold tracking-normal whitespace-nowrap rounded-md border transition-[background-color,border-color,color,box-shadow,transform] duration-med ease-snappy focus-visible:outline-none focus-visible:shadow-focus focus-visible:ring-1 focus-visible:ring-brand active:transition-none disabled:opacity-60 disabled:cursor-not-allowed disabled:pointer-events-none disabled:hover:translate-y-0';

export function Button({
  variant = 'default',
  size = 'md',
  loading = false,
  leftIcon,
  rightIcon,
  fullWidth,
  className,
  type = 'button',
  disabled,
  children,
  ...rest
}: ButtonProps) {
  const iconSize = size === 'sm' ? 14 : size === 'lg' ? 18 : 16;
  return (
    <button
      type={type}
      disabled={disabled || loading}
      className={cn(BASE, VARIANT[variant], SIZE[size], fullWidth && 'w-full', className)}
      {...rest}
    >
      {loading ? (
        <Icon as={Loader2} size={iconSize} className="animate-spin" />
      ) : leftIcon ? (
        <Icon as={leftIcon} size={iconSize} />
      ) : null}
      {children}
      {!loading && rightIcon ? <Icon as={rightIcon} size={iconSize} /> : null}
    </button>
  );
}

export interface IconButtonProps extends Omit<
  ButtonProps,
  'leftIcon' | 'rightIcon' | 'children' | 'fullWidth'
> {
  icon: ComponentType<LucideProps>;
  label: string;
}

export function IconButton({
  icon,
  label,
  size = 'md',
  variant = 'ghost',
  className,
  ...rest
}: IconButtonProps) {
  const dim = size === 'sm' ? 'w-8' : size === 'lg' ? 'w-11' : 'w-9';
  return (
    <Button
      variant={variant}
      size={size}
      aria-label={label}
      title={label}
      className={cn(dim, 'px-0', className)}
      leftIcon={icon}
      {...rest}
    />
  );
}
